import { Button, Tag, Tooltip, Typography } from 'antd';
import {
  CopyOutlined,
  ExportOutlined,
  MessageOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type {
  ConversationCustomerHelped,
  ConversationOverviewChannel,
  ConversationOverviewFlag
} from '@/api/chat/interfaces';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import { FlagTag, HelpedTag } from './tags';

const { Text } = Typography;

type ConversationDetailHeaderProps = {
  tenantName: string | null;
  channel: ConversationOverviewChannel;
  threadStatus: string;
  flag?: ConversationOverviewFlag;
  customerHelped?: ConversationCustomerHelped;
  threadId: string;
  callId: string | null;
  flowUrl: string | null;
  onBack: () => void;
  onCopy: (value: string) => void;
};

/**
 * Header of the admin conversation debug view: tenant, channel/status/flag tags, links to the
 * flow in Backstage and copy buttons for the thread and call IDs. Back returns to the filtered overview.
 */
export function ConversationDetailHeader({
  tenantName,
  channel,
  threadStatus,
  flag,
  customerHelped,
  threadId,
  callId,
  flowUrl,
  onBack,
  onCopy
}: ConversationDetailHeaderProps) {
  const { t } = useTranslation('admin');
  const channelIcon = channel === 'VOICE' ? <PhoneOutlined /> : channel === 'EMAIL' ? <MailOutlined /> : <MessageOutlined />;

  return (
    <DetailPageHeader
      icon={channelIcon}
      title={tenantName ?? t('conversationOverview.detail.notFound')}
      meta={
        <>
          <Tag color={channel === 'VOICE' ? 'cyan' : channel === 'EMAIL' ? 'purple' : 'blue'} style={{ marginInlineEnd: 0 }}>
            {channel === 'VOICE'
              ? t('conversationOverview.channel.call')
              : channel === 'EMAIL' ? t('conversationOverview.channel.email') : t('conversationOverview.channel.chat')}
          </Tag>
          <Tag style={{ marginInlineEnd: 0 }}>
            {threadStatus === 'ACTIVE'
              ? t('conversationOverview.detail.active')
              : t('conversationOverview.detail.archived')}
          </Tag>
          {flag ? <FlagTag flag={flag} /> : null}
          {customerHelped ? <HelpedTag value={customerHelped} /> : null}
        </>
      }
      actions={
        <>
          {flowUrl ? (
            <Button href={flowUrl} target="_blank" rel="noopener noreferrer" icon={<ExportOutlined />}>
              {t('conversationOverview.detail.openFlow')}
            </Button>
          ) : (
            <Text type="secondary">{t('conversationOverview.detail.noFlow')}</Text>
          )}
          <Tooltip title={threadId}>
            <Button icon={<CopyOutlined />} onClick={() => onCopy(threadId)}>
              {t('conversationOverview.detail.copyThreadId')}
            </Button>
          </Tooltip>
          {callId ? (
            <Tooltip title={callId}>
              <Button icon={<CopyOutlined />} onClick={() => onCopy(callId)}>
                {t('conversationOverview.detail.copyCallId')}
              </Button>
            </Tooltip>
          ) : null}
        </>
      }
      onBack={onBack}
      backLabel={t('conversationOverview.detail.back')}
    />
  );
}
